/**
 * J7.5 / v1.1.3-b1: client hook for the real-time hundo feed.
 *
 * Polls /api/hundo/feed every 30s while the user is signed in. The
 * HundoToast component compares the latest event id against the highest id
 * already shown (persisted to localStorage so reloads do not re-fire the
 * toast for old hundos) and renders the celebration card for any genuinely
 * new events that landed since the trainer last opened the app.
 *
 * The /api/hundo/dex endpoint is fetched on demand by the Trainer profile
 * Hundo dex section, not by this hook.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";

export interface HundoEventDTO {
  id: number;
  pokemonRecordId: number;
  speciesId: number;
  speciesName: string | null;
  source: string;
  isShiny: boolean;
  isLucky: boolean;
  isShadow: boolean;
  isPurified: boolean;
  cp: number | null;
  detectedAt: number;
  notifiedAt: number | null;
}

export interface HundoFeedResponse {
  events: HundoEventDTO[];
  nextCursor: number | null;
}

const STORAGE_KEY = "nexusdex.hundo.lastSeenId";

function readLastSeen(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeLastSeen(id: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    // best-effort
  }
}

export function useHundoFeed(opts: { enabled?: boolean; pollMs?: number } = {}) {
  const { isSignedIn, authedFetch } = useAuth();
  const enabled = (opts.enabled ?? true) && isSignedIn;
  const pollMs = opts.pollMs ?? 30_000;

  const query = useQuery<HundoFeedResponse>({
    queryKey: ["/api/hundo/feed"],
    enabled,
    staleTime: 15_000,
    refetchInterval: pollMs,
    queryFn: async () => {
      const res = await authedFetch("/api/hundo/feed?limit=25");
      if (!res.ok) throw new Error(`hundo feed failed: ${res.status}`);
      return res.json();
    },
  });

  const [lastSeen, setLastSeen] = useState<number>(() => readLastSeen());

  const events = query.data?.events ?? [];
  // Events come newest-first, so unseen events are those with id > lastSeen.
  const unseen = events.filter((e) => e.id > lastSeen);

  // Mark all current events as seen. Call this when the toast is dismissed
  // or after the celebration has been shown.
  const markAllSeen = () => {
    const top = events[0]?.id ?? 0;
    if (top > lastSeen) {
      writeLastSeen(top);
      setLastSeen(top);
    }
  };

  // On first signed-in fetch, treat anything already in the feed as seen so
  // that a fresh login does not blast the user with old hundos.
  useEffect(() => {
    if (!query.isSuccess) return;
    if (lastSeen !== 0) return;
    const top = events[0]?.id ?? 0;
    if (top > 0) {
      writeLastSeen(top);
      setLastSeen(top);
    }
  }, [query.isSuccess, events, lastSeen]);

  return {
    events,
    unseen,
    markAllSeen,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}
